<script setup lang="ts">
import FilterTriggerContent from './FilterTriggerContent.vue'
import type { useFilterPopoverState } from '~/composables/filter/useFilterPopoverState'

defineProps<{
  filterPopoverState: ReturnType<typeof useFilterPopoverState>
}>()

const slots = useSlots()
</script>

<template>
  <IPopover
    events="manual"
    :visible="filterPopoverState.isOpen.value"
    :interactable="true"
    :offset="13"
    :class="[
      'c-filter-popover',
      filterPopoverState.isOpen.value ? 'is-open' : '',
    ]"
    placement="bottom-end"
    @click:outside="filterPopoverState.hide()"
  >
    <IButton
      class="c-filter-popover__trigger"
      @click="filterPopoverState.toggle()"
    >
      <FilterTriggerContent>
        <template v-if="slots['trigger-label']" #label>
          <slot name="trigger-label" />
        </template>

        <template v-if="slots['trigger-value']" #value>
          <slot name="trigger-value" />
        </template>
      </FilterTriggerContent>
    </IButton>

    <template #body>
      <div class="c-filter-popover__body">
        <slot name="popover-body" />

        <div class="c-filter-popover__actions">
          <IButton
            color="secondary"
            size="sm"
            @click="filterPopoverState.hide()"
          >
            {{ $t('filter.submit') }}
          </IButton>
        </div>
      </div>
    </template>
  </IPopover>
</template>

<style lang="scss">
@use '@inkline/inkline/css/mixins' as *;

@include breakpoint-up('lg') {
  .c-estate-listing-filter {
    &:has(.c-filter-popover.is-open),
    &:has(.c-filter-popover .popover[class*='transition-enter']),
    &:has(.c-filter-popover .popover[class*='transition-leave']) {
      position: relative;
      z-index: 199;
    }
  }
}
</style>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

:deep(.popover) {
  width: max-content;

  &.zoom-in-top-transition-enter-active,
  &.zoom-in-top-transition-leave-active,
  &.zoom-in-top-transition-enter-from {
    transform: unset;
  }

  .popover-body {
    min-width: 220px;
  }

  @include breakpoint-down('md') {
    width: 100%;
    display: block !important;
    position: static;
    box-shadow: none;

    .arrow {
      display: none;
    }

    .popover-body {
      border: none;
    }
  }
}

:deep(.popover-trigger) {
  @include breakpoint-down('md') {
    display: flex;
  }
}

.c-filter-popover {
  z-index: 199;

  @include breakpoint-down('md') {
    &:not(:first-child) {
      border-top: 1px solid var(--color-border);
      padding-top: 24px;
      margin-top: 24px;
    }
  }

  &__trigger {
    padding: 0;

    @include breakpoint-down('md') {
      display: none;
    }
  }

  --popover--border-top-left-radius: 0;
  --popover--border-top-right-radius: 0;
  --popover--border-bottom-left-radius: 0;
  --popover--border-bottom-right-radius: 0;

  --popover--body--border-color: var(--color-green-primary-light-2);
  --popover--body--padding: 0;

  &__body {
    --form-group--margin-top: 0px;

    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    @include breakpoint-down('md') {
      padding: 0;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 4px;

    @include breakpoint-down('md') {
      display: none;
    }
  }
}
</style>
